import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box as MuiBox } from "@mui/material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import { useAlerts } from "common";
import { LoadingGridOverlay } from "common/components/DataGrid/LoadingGridOverlay";
import { NoRowsGridOverlay } from "common/components/DataGrid/NoRowsGridOverlay";
import { LOADER_TYPES } from "app/shared/constants";
import { useActivityLogContext } from "app/services/activityLogContext";
import { getAllActivityLogs } from "app/services/activityLogService";
import { defaultTableColumnHeaders } from "./ActivityLogTableConfig";
import { tableStyles } from "app/shared/UI/sharedStyles";
import JsonDataModal from "./JsonDataModal";

const ActivityLogPageContent = () => {
  const { filters, initialLoad, pagination, setPagination, sort, setSort } =
    useActivityLogContext();

  const { t } = useTranslation();
  const [activityLogs, setActivityLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const { setAlert } = useAlerts();

  const initialLoading = {
    activityLogData: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  useEffect(() => {
    getAllActivityLogs(
      setActivityLogs,
      setTotalCount,
      setAlert,
      filters,
      pagination,
      sort,
      setLoader
    );
    // eslint-disable-next-line
  }, [pagination, sort, filters]);

  const handleViewJson = (row) => {
    setSelectedLog(row);
    setJsonModalOpen(true);
  };

  const handleCloseJsonModal = () => {
    setJsonModalOpen(false);
    setSelectedLog(null);
  };

  const NoActivityLogOverlay = () =>
    !initialLoad ? (
      <NoRowsGridOverlay message={t("activityLog.emptyTable")} />
    ) : (
      <NoRowsGridOverlay message={t("activityLog.initialLoadMessage")} />
    );

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      setSort({
        orderBy: sortModel[0].field,
        order: sortModel[0].sort,
      });
    }
  };

  const handlePaginationModelChange = (model) => {
    setPagination({
      ...pagination,
      page: model.page,
      rowsPerPage: model.pageSize,
    });
  };

  return (
    <>
      <MuiBox
        id="pep-activity-log-table-box"
        sx={{ width: "100%", overflow: "auto", pb: 2 }}
      >
        <MuiDataGrid
          slots={{
            loadingOverlay: LoadingGridOverlay,
            noRowsOverlay: NoActivityLogOverlay,
          }}
          rows={activityLogs}
          getRowId={(row) => row.id}
          columns={defaultTableColumnHeaders(t, handleViewJson)}
          autoHeight
          pagination
          disableColumnMenu
          disableColumnResize
          disableColumnReorder
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          loading={isLoading[LOADER_TYPES.ACTIVITY_LOG_DATA]}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{
            page: pagination.page,
            pageSize: pagination.rowsPerPage,
          }}
          rowCount={totalCount}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={[{ field: sort.orderBy, sort: sort.order }]}
          sortingOrder={["asc", "desc"]}
          onSortModelChange={handleSortChange}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
          }
          sx={tableStyles}
          columnHeaderHeight={60}
          getRowHeight={() => "auto"}
        />
      </MuiBox>

      <JsonDataModal
        open={jsonModalOpen}
        onClose={handleCloseJsonModal}
        data={selectedLog}
        title={
          selectedLog
            ? `${t("activityLog.jsonModal.title")} - ${selectedLog.action}`
            : null
        }
      />
    </>
  );
};

export default ActivityLogPageContent;
